import { useCallback, useEffect, useRef, useState } from "react";
import { api, type CalendarView, type CalendarEvent } from "../api.ts";
import { C, mono, sans, addDays, todayISO } from "../theme.ts";
import { useRefresh } from "../components/PullToRefresh.tsx";
import { Segment } from "../components/glass/Segment.tsx";

// The three calendars on one surface, read-only.
//
// This screen never writes to a calendar. There is no add button, no drag to
// move an event, no cancel — that decision was made on 22.08.2026, and writing
// is the thing that would need OAuth, production publishing and sensitive-
// scope verification. When something has to change, it changes in Google.
//
// Conflicts do not appear here as a banner. They are turned into tasks by the
// poller, and they show up in the brief and on the board like anything else.

const DAYS_SHOWN = 7;

const dayName = (iso: string) =>
  new Date(`${iso}T00:00:00Z`).toLocaleDateString("en-GB", {
    weekday: "long", day: "numeric", month: "short", timeZone: "UTC",
  });

type Mode = "agenda" | "week";
const MODE_KEY = "mc-calendar-mode";
const readMode = (): Mode => {
  try { return localStorage.getItem(MODE_KEY) === "week" ? "week" : "agenda"; } catch { return "agenda"; }
};

/** One event, the same card in the agenda and in a week column. */
function EventCard({ e, colour }: { e: CalendarEvent; colour: string }) {
  return (
    <div
      style={{
        background: e.busy ? C.card : C.cardQuiet,
        borderRadius: 10,
        border: `1px solid ${C.rule}`,
        borderLeft: `4px solid ${colour}`,
        padding: "9px 12px",
        marginBottom: 6,
        opacity: e.busy ? 1 : 0.7,
      }}
    >
      <div style={{ fontSize: 13.5, lineHeight: 1.4 }}>{e.summary ?? "untitled"}</div>
      <div style={{ fontFamily: mono, fontSize: 11, color: C.inkFaint, marginTop: 3 }}>
        {e.all_day ? "all day" : `${e.from_time}–${e.to_time}`}
        {e.location ? ` · ${e.location}` : ""}
        {e.busy ? "" : " · free"}
      </div>
    </div>
  );
}

export function Calendar() {
  const [start, setStart] = useState(todayISO());
  const [view, setView] = useState<CalendarView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showSources, setShowSources] = useState(false);
  const [mode, setModeState] = useState<Mode>(readMode);
  const setMode = (m: Mode) => {
    setModeState(m);
    try { localStorage.setItem(MODE_KEY, m); } catch { /* the choice just is not remembered */ }
  };
  const weekRef = useRef<HTMLDivElement>(null);
  // A new window starts back at its first column.
  useEffect(() => { weekRef.current?.scrollTo({ left: 0 }); }, [start, mode]);

  const end = addDays(start, DAYS_SHOWN - 1);

  const load = useCallback(async () => {
    try {
      setView(await api.calendar(start, end));
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [start, end]);

  useEffect(() => { load(); }, [load]);

  // A pull re-reads the window on screen. It does not poll the feeds — that is
  // the button below, and it is a different, slower thing.
  useRefresh(load);

  const refresh = async () => {
    setBusy(true);
    // Reading, not writing. The feeds are unaffected by this.
    try { await api.pollCalendar(); await load(); }
    catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  const colourOf = (source: string) =>
    view?.sources.find((s) => s.id === source)?.colour ?? C.ruleStrong;

  const days = Array.from({ length: DAYS_SHOWN }, (_, i) => addDays(start, i));
  const today = todayISO();

  const onDay = (iso: string): CalendarEvent[] =>
    (view?.events ?? [])
      .filter((e) => e.on_day <= iso && e.end_day >= iso)
      .sort((a, b) => (a.all_day === b.all_day ? a.starts_at.localeCompare(b.starts_at) : a.all_day ? -1 : 1));

  const anyConfigured = view?.sources.some((s) => s.configured) ?? false;

  const arrow = (dir: -1 | 1) => (
    <button
      onClick={() => setStart(addDays(start, dir * DAYS_SHOWN))}
      aria-label={dir < 0 ? "earlier" : "later"}
      style={{
        width: 32, height: 32, borderRadius: 8, border: `1px solid ${C.ruleStrong}`,
        background: C.card, color: C.inkSoft, cursor: "pointer", fontFamily: sans, fontSize: 14,
      }}
    >{dir < 0 ? "‹" : "›"}</button>
  );

  return (
    <div style={{ padding: "0 18px 8px" }}>
      <div style={{ marginBottom: 12 }}>
        <Segment
          options={[{ key: "agenda", label: "Agenda" }, { key: "week", label: "Week" }]}
          value={mode}
          onChange={setMode}
        />
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
        {arrow(-1)}
        {arrow(1)}
        <button
          onClick={() => setStart(todayISO())}
          style={{
            fontFamily: mono, fontSize: 11, color: C.inkFaint, background: "none",
            border: "none", cursor: "pointer", padding: "0 4px",
          }}
        >today</button>
        <button
          onClick={() => setShowSources(!showSources)}
          style={{
            marginLeft: "auto", fontFamily: mono, fontSize: 11, color: C.inkGhost,
            background: "none", border: "none", cursor: "pointer",
          }}
        >feeds</button>
      </div>

      {error && <p style={{ color: C.alert, fontSize: 13 }}>{error}</p>}

      {showSources && view && (
        <div style={{ background: C.cardQuiet, borderRadius: 12, border: `1px solid ${C.rule}`, padding: "12px 14px", marginBottom: 14 }}>
          {view.sources.map((s) => (
            <div key={s.id} style={{ display: "flex", gap: 8, alignItems: "baseline", marginBottom: 8 }}>
              <span style={{ width: 8, height: 8, borderRadius: 99, background: s.colour, flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13 }}>{s.label}</div>
                <div style={{ fontFamily: mono, fontSize: 10.5, color: C.inkFaint, marginTop: 2, lineHeight: 1.6 }}>
                  {!s.configured
                    ? "no address supplied yet — T-131"
                    : s.last_poll
                      ? s.last_poll.ok
                        ? `${s.last_poll.events} events · read ${new Date(s.last_poll.ran_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}`
                        : `did not answer${s.last_poll.status ? ` · ${s.last_poll.status}` : ""}${s.last_poll.error ? ` · ${s.last_poll.error}` : ""}`
                      : "not read yet"}
                </div>
              </div>
            </div>
          ))}
          <button
            onClick={refresh}
            disabled={busy || !anyConfigured}
            style={{
              fontFamily: mono, fontSize: 11, color: C.inkSoft, background: "none",
              border: `1px solid ${C.ruleStrong}`, borderRadius: 8, padding: "5px 10px",
              cursor: anyConfigured ? "pointer" : "default", opacity: anyConfigured ? 1 : 0.4,
            }}
          >{busy ? "reading…" : "read now"}</button>
          <p style={{ fontFamily: mono, fontSize: 10.5, color: C.inkGhost, lineHeight: 1.7, margin: "10px 0 0" }}>
            read-only · this app never writes to a calendar
          </p>
        </div>
      )}

      {!anyConfigured && (
        <p style={{ fontSize: 13, color: C.inkSoft, lineHeight: 1.6, margin: "0 0 16px" }}>
          No feed addresses yet. Three secret iCal addresses go into the server's
          environment and the calendars appear here — that is T-131.
        </p>
      )}

      {mode === "agenda" && days.map((d) => {
        const events = onDay(d);
        return (
          <div key={d} style={{ marginBottom: 16 }}>
            <p style={{
              fontFamily: mono, fontSize: 11, letterSpacing: 1,
              color: d === today ? C.inkSoft : C.inkFaint,
              margin: "0 0 6px",
            }}>
              {d === today ? "TODAY" : dayName(d).toUpperCase()}
            </p>

            {events.length === 0 ? (
              // Nothing scheduled is a fact, not an absence to apologise for.
              <p style={{ fontFamily: mono, fontSize: 11, color: C.inkGhost, margin: 0 }}>—</p>
            ) : (
              events.map((e) => <EventCard key={e.id} e={e} colour={colourOf(e.source)} />)
            )}
          </div>
        );
      })}

      {/* The week: one column per day, each as wide as the screen less a
          margin, so one column and a sliver of the next are always in view.
          The scroller runs edge to edge (negative margin cancels the page
          gutter) and snaps each column to the gutter. Widths are a share of
          the scroller, never a pixel count, so this holds from 360 to 480. */}
      {mode === "week" && (
        <div
          ref={weekRef}
          style={{
            display: "flex", gap: 10, margin: "0 -18px", padding: "0 18px 8px",
            overflowX: "auto", scrollSnapType: "x mandatory", scrollPaddingLeft: 18,
            overscrollBehaviorX: "contain", scrollbarWidth: "none",
          }}
        >
          {days.map((d) => {
            const events = onDay(d);
            return (
              <div key={d} style={{ flex: "0 0 calc(100% - 44px)", minWidth: 0, scrollSnapAlign: "start" }}>
                <p style={{
                  fontFamily: mono, fontSize: 11, letterSpacing: 1,
                  color: d === today ? C.inkSoft : C.inkFaint,
                  margin: "0 0 6px",
                }}>
                  {d === today ? "TODAY" : dayName(d).toUpperCase()}
                </p>
                {events.length === 0 ? (
                  <p style={{ fontFamily: mono, fontSize: 11, color: C.inkGhost, margin: 0 }}>—</p>
                ) : (
                  events.map((e) => <EventCard key={e.id} e={e} colour={colourOf(e.source)} />)
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
